import React, { useEffect, useMemo, useRef, useState, Children, ReactElement, cloneElement, isValidElement } from "react";
import classNames from "classnames";
import { getOtherProperties } from "../common/Utils";
import {
  setTransitionDuration,
  addEventListener,
  removeEventListener,
  parents,
} from "../common/Dom";
import { preloadImages } from "./LoadImage";
import { SwiperProps, SwiperState } from "./PropsType";

export default function Swiper(props: SwiperProps) {
  const {
    className,
    children: childrenProps,
    navigation,
    autoplay = false,
    autoplayDisableOnInteraction = false,
    centerMode = false,
    direction = "horizontal",
    easing = "ease",
    effect = "slide",
    initialSlide = 0,
    loop = false,
    observe = false,
    observeParents = false,
    on = {},
    pagination = true,
    paginationClickable = false,
    prefixCls = "bm-Swiper",
    spaceBetween = 0,
    speed = 300,
    delay = 3000,
    touch = true,
    updateOnImagesReady,
    ...other
  } = props;
  const [state, setState] = useState<SwiperState>({
    activeIndex: props.activeIndex || 0,
    opacity: 1,
  });
  const [position, setPosition] = useState({
    x: 0,
    y: 0,
  });
  const [rect, setRect] = useState({
    width: 0,
    height: 0,
  });
  const wrapper = useRef(null);
  const slider = useRef(null);
  const isMobile: boolean = "ontouchstart" in document;
  const dragging = useRef(false);
  const autoPlayTimer = useRef<NodeJS.Timeout | null>(null);
  const count = useRef(Children.count(childrenProps) || 0);
  const touches = useRef<any>({});
  const isRunning = useRef(false);
  const imagesLoaded = useRef(0);
  const centerOffset = useRef(0);
  let observers: Array<any> = [];
  const isHorizontal = () => {
    return direction === "horizontal";
  };

  const getCurrentSlide = (slide: number) => {
    let index: number = slide;
    if (index < 0) {
      index = 0;
    } else {
      index = index % count.current;
    }
    return index;
  };

  const slideTo = (index: number, speed: number = props.speed!) => {
    isRunning.current = true;
    const { width, height } = rect;
    let styles: any;
    if (!loop) {
      index = getCurrentSlide(index);
    }
    setTransitionDuration(slider.current, speed);
    let activeIndex = index;
    if (effect === "fade") {
      if (activeIndex < 0) {
        activeIndex = count.current - 1;
      } else if (activeIndex >= count.current) {
        activeIndex = 0;
      }
    }
    if (isHorizontal()) {
      styles = {
        activeIndex,
        opacity: 1,
      };
      setPosition({
        ...position,
        x: -(width + spaceBetween!)! * activeIndex + centerOffset.current,
      });
    } else {
      styles = {
        activeIndex,
        opacity: 1,
      };
      setPosition({
        ...position,
        y: -(height! + spaceBetween!) * activeIndex,
      });
    }
    if (props.on && props.on.transitionStart!) {
      (props.on as any).transitionStart(index, this);
    }
    setState({
      ...state,
      ...styles,
    });
    if (!speed) {
      isRunning.current = false;
    }
  };

  const slidePrev = (disabled: boolean = true) => {
    if (disabled) {
      return;
    }
    let { activeIndex } = state;
    activeIndex--;
    slideTo(activeIndex);
  };

  const slideNext = (disabled: boolean = true) => {
    if (disabled) {
      return;
    }
    let { activeIndex } = state;
    activeIndex++;
    slideTo(activeIndex);
  };

  const handleNavigation = (disabled: boolean, next: boolean, event: any) => {
    event.stopPropagation();
    if (disabled) {
      return;
    }
    if (next) {
      slideNext(disabled);
    } else {
      slidePrev(disabled);
    }
  };

  const getPoint = (e: any) => {
    const touchEvent = isMobile ? e.changedTouches[0] : e;
    return {
      x: touchEvent.pageX || touchEvent.clientX,
      y: touchEvent.pageY || touchEvent.clientY,
    };
  };

  const swipeStart = (event: any) => {
    if (touch && !isRunning.current) {
      event.stopPropagation();
      const { x, y } = getPoint(event);
      dragging.current = true;
      setTransitionDuration(slider.current, 0);
      if (autoplayDisableOnInteraction) {
        clearAutoplay();
      }
      touches.current.startX = x;
      touches.current.startY = y;
    }
  };

  const swipeMove = (event: any) => {
    event.stopPropagation();
    if (dragging.current && !isRunning.current) {
      const { x, y } = getPoint(event);
      const diffX = x - touches.current.startX;
      const diffY = y - touches.current.startY;
      const { activeIndex }: any = state;
      const { width, height }: any = rect;
      if (effect === "slide") {
        const maxTranslate = isHorizontal() ? width / 2 : height / 2;
        const minTranslate = 20;
        const diff = isHorizontal() ? diffX : diffY;
        const pos = (diff / width) * (maxTranslate - minTranslate);
        let translate = isHorizontal()
          ? -(width + spaceBetween!) * activeIndex + pos
          : -(height + spaceBetween!) * activeIndex + pos;
        const setTranslate = (size: number) => {
          const collapseEnd = size * (count.current - 1) + maxTranslate;
          if (!loop) {
            if (translate > maxTranslate) {
              translate = maxTranslate;
            } else if (Math.abs(translate) > collapseEnd) {
              translate = -collapseEnd;
            }
          }
        };
        if (isHorizontal()) {
          setTranslate(width);
          setPosition({
            ...position,
            x: translate + centerOffset.current,
          });
        } else {
          setTranslate(height);
          setPosition({
            ...position,
            y: translate,
          });
        }
        console.log(translate);
      } else {
        const opacity = isHorizontal()
          ? Math.abs(diffX) / width
          : Math.abs(diffY) / height;
        setState({
          ...state,
          opacity,
        });
      }
      touches.current.diffX = diffX;
      touches.current.diffY = diffY;
      touches.current.currentX = x;
      touches.current.currentY = y;
    }
  };

  const swipeEnd = (event: any) => {
    event.stopPropagation();
    if (dragging.current && !isRunning.current) {
      const { width, height }: any = rect;
      let activeIndex: any = state.activeIndex;
      const { diffX, diffY } = touches.current;
      const diffWidth = width / 5;
      const diffHeight = height / 5;
      const getActiveIndex = (diff: any, dist: any) => {
        if (Math.abs(diff) > dist) {
          if (diff < 0 && (loop || activeIndex !== count.current - 1)) {
            activeIndex++;
          } else if (diff > 0 && (loop || activeIndex !== 0)) {
            activeIndex--;
          }
        }
      };
      if (isHorizontal()) {
        getActiveIndex(diffX, diffWidth);
      } else {
        getActiveIndex(diffY, diffHeight);
      }
      if (props.on && props.on.slideChange!) {
        (props.on as any).slideChange(activeIndex, this);
      }
      if (effect === "fade") {
        if (activeIndex < 0) {
          activeIndex = count.current - 1;
        } else if (activeIndex >= count.current) {
          activeIndex = 0;
        }
      }
      slideTo(activeIndex);
    }
    dragging.current = false;
  };

  const getBulletItem = (bullets: number[]) => {
    return bullets.map((v: number) => {
      if (typeof pagination === "function") {
        return pagination(v);
      }
      const styleClass = classNames(`${prefixCls}-pagination-bullet`, {
        [`${prefixCls}-pagination-bullet-active`]: state.activeIndex === v,
        [`${prefixCls}-pagination-clickable`]: paginationClickable,
      });
      let event = {};
      if (paginationClickable) {
        event = {
          onMouseDown: !isMobile ? paginationClick.bind(this, v) : null,
          onTouchStart: isMobile ? paginationClick.bind(this, v) : null,
        };
      }
      return <div key={v} {...event} className={styleClass} />;
    });
  };

  const getNavigation = () => {
    const nav: any = props.navigation;
    const { activeIndex } = state;
    const toPrev: boolean = activeIndex <= 0 && !loop;
    const toNext: boolean = activeIndex >= count.current - 1 && !loop;
    const prevClass = classNames(`${prefixCls}-prev`, {
      [`${prefixCls}-disabled`]: toPrev,
    });
    const nextClass = classNames(`${prefixCls}-next`, {
      [`${prefixCls}-disabled`]: toNext,
    });
    const prevEvent = {
      onMouseDown: !isMobile
        ? handleNavigation.bind(this, toPrev, false)
        : null,
      onTouchStart: isMobile
        ? handleNavigation.bind(this, toPrev, false)
        : null,
    };
    const nextEvent = {
      onMouseDown: !isMobile ? handleNavigation.bind(this, toNext, true) : null,
      onTouchStart: isMobile ? handleNavigation.bind(this, toNext, true) : null,
    };
    const prevEl = (
      <div key="prev" className={prevClass} {...prevEvent}>
        {nav.prevEl}
      </div>
    );
    const nextEl = (
      <div key="next" className={nextClass} {...nextEvent}>
        {nav.nextEl}
      </div>
    );
    return [prevEl, nextEl];
  };

  const paginationClick = (index: number) => {
    if (autoplayDisableOnInteraction) {
      clearAutoplay();
    }
    slideTo(index);
  };

  const updateSize = (size: any, slideSize: any) => {
    if (size) {
      if (isNaN(Number(size))) {
        if (size.indexOf("%")) {
          slideSize *= size / 100;
        } else {
          slideSize = parseFloat(size);
        }
      } else {
        if (size < 1) {
          slideSize *= size;
        } else {
          slideSize = size;
        }
      }
    }
    return slideSize;
  };

  const reset = (cb?: any) => {
    if (
      !slider.current ||
      !(slider.current as HTMLDivElement)?.firstElementChild
    ) {
      return;
    }
    if (wrapper.current) {
      const height = ((slider.current as HTMLDivElement)?.firstElementChild as any)
        ?.offsetHeight;
      const slideWidth: any = props.slideWidth!;
      const slideHeight: any = props.slideHeight!;
      const width = (wrapper.current as HTMLDivElement).offsetWidth;
      if (props.centerMode) {
        centerOffset.current = Math.round((width * (1 - slideWidth)) / 2);
      }
      setRect({
        width: updateSize(slideWidth, width),
        height: updateSize(slideHeight, height),
      });
      if (cb) {
        cb();
      }
    }
  };

  const startAutoplay = () => {
    if (autoPlayTimer.current) {
      return false;
    }
    autoPlayTimer.current = setInterval(() => {
      setTransitionDuration(slider.current, speed);
      slideNext(false);
    }, delay);
  };

  const clearAutoplay = () => {
    if (autoPlayTimer.current) {
      clearInterval(autoPlayTimer.current);
      autoPlayTimer.current = null;
    }
  };

  const onWindowResized = () => {
    const { activeIndex } = state;
    reset(() => {
      slideTo(getCurrentSlide(activeIndex), 0);
    });
  };

  const onTransitionEnd = () => {
    let { activeIndex } = state;
    if (activeIndex < 0) {
      activeIndex = count.current - 1;
    } else if (activeIndex >= count.current) {
      activeIndex = 0;
    }
    if (props.on && props.on.transitionEnd!) {
      (props.on as any).transitionEnd(activeIndex, this);
    }
    slideTo(activeIndex, 0);
    if (autoplay && !autoplayDisableOnInteraction) {
      startAutoplay();
    }
    isRunning.current = false;
  };

  const attach = (target: any) => {
    const win: any = window;
    const MutationObserver =
      win.MutationObserver ||
      win.WebKitMutationObserver ||
      win.MozMutationObserver;

    const observer = new MutationObserver((mutations) => {
      mutations.forEach(() => {
        reset();
      });
    });

    // 配置观察选项:
    const config = { attributes: true, childList: true, characterData: true };

    // 传入目标节点和观察选项
    observer.observe(target, config);
    return observer;
  };

  const observer = () => {
    if (observe && wrapper.current) {
      // 创建观察者对象
      observers.push(attach((wrapper.current as HTMLDivElement).parentNode));
    }

    if (observeParents) {
      const containerParents = parents(wrapper.current);
      for (const container of containerParents) {
        observers.push(attach(container));
      }
    }
  };
  // const horizontal: boolean = isHorizontal();
  const styleClass = classNames(
    prefixCls,
    className,
    `${prefixCls}-${direction}`
  );
  const bullets: number[] = [];
  const { activeIndex } = state;
  const { width, height } = rect;
  const isFade: boolean = effect === "fade";
  const children = Children.map(
    childrenProps,
    (child: ReactElement<any>, index: number) => {
      if (!isValidElement(child)) {
        return;
      }
      const horizontal = isHorizontal();
      let opacity = 0;
      let left: number = horizontal ? index * width! : 0,
        top: number = !horizontal ? index * height! : 0;
      const { diffX, diffY } = touches.current,
        childStyles: any = {};
      if (!isFade) {
        if (loop && count.current > 2) {
          if (count.current <= activeIndex + 1 && index === 0) {
            horizontal
              ? (left = (width + spaceBetween!) * count.current)
              : (top = height! * count.current);
            childStyles.transform = `translate3d(${left}px, ${top}px, 0)`;
            childStyles.WebkitTransform = `translate3d(${left}px, ${top}px, 0)`;
          } else if (activeIndex <= 0 && count.current === index + 1) {
            horizontal
              ? (left = -(width + spaceBetween!) * count.current)
              : (top = -height! * count.current);
            childStyles.transform = `translate3d(${left}px, ${top}px, 0)`;
            childStyles.WebkitTransform = `translate3d(${left}px, ${top}px, 0)`;
          }
        }
        opacity = 1;
      } else {
        childStyles.transform = `translate3d(-${left}px, -${top}px, 0)`;
        childStyles.WebkitTransform = `translate3d(-${left}px, -${top}px, 0)`;
        if (dragging.current) {
          const diff = horizontal ? diffX : diffY;
          if (
            loop &&
            ((activeIndex <= 0 && diff > 0 && count.current === index + 1) ||
              (count.current >= activeIndex + 1 && diff < 0 && index === 0))
          ) {
            opacity = state.opacity!;
          }
          if (
            diff > 0 &&
            activeIndex > 0 &&
            activeIndex < count.current &&
            activeIndex - 1 === index
          ) {
            opacity = state.opacity!;
          }
          if (
            diff < 0 &&
            activeIndex >= 0 &&
            activeIndex < count.current - 1 &&
            activeIndex + 1 === index
          ) {
            opacity = state.opacity!;
          }
        }
        if (index === activeIndex) {
          opacity = 1;
        }
      }
      if (isFade) {
        let duration = speed;
        if (dragging.current) {
          duration = 0;
        }
        childStyles.transition = `opacity ${duration}ms ${easing}`;
        childStyles.WebkitTransition = `opacity ${duration}ms ${easing}`;
      }
      const childClass = classNames(`${prefixCls}-slide`, {
        [`${prefixCls}-slide-active`]: activeIndex === index,
      });
      if (!isFade && spaceBetween! > 0) {
        childStyles.marginRight = spaceBetween;
      }
      childStyles.width = width;
      childStyles.opacity = opacity;
      bullets.push(index);
      return cloneElement(
        <div className={childClass} key={index} style={childStyles}>
          {child}
        </div>,
        {}
      );
    }
  );
  const otherProps: any = getOtherProperties(other, [
    "activeIndex",
    "autoplayDisableOnInteraction",
    "autoplay",
    "centerMode",
    "delay",
    "initialSlide",
    "observeParents",
    "observe",
    "paginationClickable",
    "slideWidth",
    "touchRatio",
    "touch",
  ]);

  const offset = useMemo(() => {
    let x: any = position.x;
    let y: any = position.y;
    if (effect === "fade") {
      x = y = 0;
    }

    return {
      x,
      y,
    };
  }, [effect, position]);

  const styles = useMemo(() => {
    return effect === "fade"
      ? {}
      : {
          transform: isHorizontal()
            ? `translate3d(${offset.x}px, 0, 0)`
            : `translate3d(0, ${offset.y}px, 0)`,
          WebkitTransform: isHorizontal()
            ? `translate3d(${offset.x}px, 0, 0)`
            : `translate3d(0, ${offset.y}px, 0)`,
          msTransform: isHorizontal()
            ? `translate3d(${offset.x}px, 0, 0)`
            : `translate3d(0, ${offset.y}px, 0)`,
          WebkitTransition: `-webkit-transform ${speed}ms ${easing}`,
          transition: `transform ${speed}ms ${easing}`,
        };
  }, [effect, offset]);

  useEffect(() => {
    count.current = Children.count(childrenProps) || 0;
  }, [props.children]);

  useEffect(() => {
    if (props.on && props.on.init!) {
      (props.on as any).init();
    }
    if (autoplay) {
      startAutoplay();
    }
    if (updateOnImagesReady) {
      preloadImages(
        wrapper.current,
        imagesLoaded.current,
        () => {
          if (props.on && props.on.imagesReady!) {
            (props.on as any).imagesReady();
          }
        },
        (loaded: number) => {
          imagesLoaded.current = loaded;
        }
      );
    }
    if (window) {
      addEventListener(window, "resize", onWindowResized);
    }

    return () => {
      touches.current = {};
      clearAutoplay();
      if (observers.length) {
        for (const observer of observers) {
          observer.disconnect();
        }
      }
      removeEventListener(window, "resize", onWindowResized);
    };
  }, []);

  useEffect(() => {
    if (wrapper.current) {
      reset(() => {
        slideTo(getCurrentSlide(initialSlide!), 0);
      });
      if (autoplay) {
        observer();
      }
    }
  }, []);

  useEffect(() => {
    if (typeof props.activeIndex === "number") {
      slideTo(props.activeIndex);
    }
  }, [props.activeIndex]);

  return (
    <div className={styleClass} {...otherProps}>
      <div
        className={`${prefixCls}-wrapper`}
        ref={wrapper}
        style={{ height: isHorizontal() ? "100%" : height! }}
        onMouseDown={isMobile ? () => false : swipeStart}
        onMouseMove={isMobile ? () => false : swipeMove}
        onMouseUp={isMobile ? () => false : swipeEnd}
        onMouseLeave={isMobile ? () => false : swipeEnd}
        onTouchStart={isMobile ? swipeStart : () => false}
        onTouchMove={isMobile ? swipeMove : () => false}
        onTouchEnd={isMobile ? swipeEnd : () => false}
      >
        <div
          className={`${prefixCls}-list`}
          style={styles}
          onTransitionEnd={onTransitionEnd}
          ref={slider}
        >
          {children}
        </div>
      </div>
      {pagination ? (
        <div className={`${prefixCls}-pagination`}>
          <div className={`${prefixCls}-pagination-bullets`}>
            {getBulletItem(bullets) as any}
          </div>
        </div>
      ) : null}
      {navigation ? getNavigation() : null}
    </div>
  );
}
